import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Award, ArrowLeft, Lock, Share2, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_ACHIEVEMENTS = [
  { id: 'first_quiz', title: 'Перший квіз', description: 'Завершити перший швидкий квіз', icon: '🎯' },
  { id: 'microservices_guru', title: 'Microservices Guru', description: 'Відповісти правильно на 10 питань про мікросервіси', icon: '🌟' },
  { id: 'ddd_master', title: 'DDD Master', description: 'Освоїти принципи Domain-Driven Design', icon: '🎯' },
  { id: 'scalability_wizard', title: 'Scalability Wizard', description: 'Вирішити 5 сценаріїв масштабованості', icon: '🚀' },
  { id: 'tradeoff_genius', title: 'Trade-off Genius', description: 'Знайти ідеальний баланс в 10 викликах', icon: '⚖️' },
  { id: 'architect_legend', title: 'Architect Legend', description: 'Досягти 10 рівня', icon: '🏆' },
  { id: 'perfect_streak', title: 'Perfect Streak', description: 'Відповісти правильно на 10 питань підряд', icon: '🔥' },
  { id: 'speed_demon', title: 'Speed Demon', description: 'Відповісти на питання за 5 секунд', icon: '⚡' },
];

export default function Achievements() {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharingId, setSharingId] = useState(null);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const handleShareAchievement = async (achievement) => {
    if (!achievement.isUnlocked) {
      toast.error("Спочатку розблокуйте це досягнення!");
      return;
    }
    
    setSharingId(achievement.id);
    
    try {
      // Get share data from backend
      const response = await fetch(`${BACKEND_URL}/api/share/achievement/${achievement.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to get share data');
      }
      
      const shareData = await response.json();
      
      // Create LinkedIn share URL
      const shareText = `🏅 Нове досягнення розблоковано в "Архітектор Майбутнього"!\n\n${achievement.icon} ${achievement.title}\n${achievement.description}\n\n#SoftwareArchitecture #Achievement #TechSkills`;
      const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&summary=${encodeURIComponent(shareText)}`;
      
      // Open LinkedIn share dialog
      const width = 600;
      const height = 600;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      
      window.open(
        shareUrl,
        'linkedin-share',
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0`
      );
      
      toast.success("Відкрито LinkedIn для шерингу!");
    } catch (error) {
      console.error('Share error:', error);
      // Fallback: share without backend data
      const shareText = `🏅 Розблоковано досягнення "${achievement.title}" в грі "Архітектор Майбутнього"! #SoftwareArchitecture`;
      const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
      
      window.open(shareUrl, 'linkedin-share', 'width=600,height=600');
      toast.success("Відкрито LinkedIn для шерингу!");
    } finally {
      setSharingId(null);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/achievements`, {
        credentials: 'include'
      });
      const data = await response.json();
      setAchievements(data);
    } catch (error) {
      toast.error('Помилка завантаження досягнень');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const unlockedIds = achievements.map(a => a.achievement_id);

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">Досягнення</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="text-center mb-8">
              <Award className="w-16 h-16 text-secondary mx-auto mb-4" />
              <h2 className="text-4xl font-black text-foreground mb-2">Ваші Досягнення</h2>
              <p className="text-lg text-muted-foreground">
                Розблоковано: {achievements.length} / {ALL_ACHIEVEMENTS.length}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ALL_ACHIEVEMENTS.map((achievement, idx) => {
                const isUnlocked = unlockedIds.includes(achievement.id);
                return (
                  <motion.div
                    key={achievement.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-card/50 backdrop-blur-sm border p-6 rounded-xl ${
                      isUnlocked
                        ? 'border-secondary/50 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                        : 'border-border/50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`text-5xl ${
                        isUnlocked ? '' : 'grayscale opacity-40'
                      }`}>
                        {isUnlocked ? achievement.icon : <Lock className="w-12 h-12 text-muted-foreground" />}
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-lg font-bold mb-2 ${
                          isUnlocked ? 'text-foreground' : 'text-muted-foreground'
                        }`}>
                          {achievement.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">{achievement.description}</p>
                        {isUnlocked && (
                          <div className="mt-3 flex items-center space-x-2">
                            <span className="inline-block px-3 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-bold">
                              Розблоковано
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleShareAchievement({ ...achievement, isUnlocked })}
                              disabled={sharingId === achievement.id}
                              className="p-2 h-auto hover:bg-blue-500/20 hover:text-blue-400 transition-colors"
                              data-testid={`share-achievement-${achievement.id}`}
                            >
                              {sharingId === achievement.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full" />
                              ) : (
                                <Linkedin className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}